import { useState, useEffect, useRef, type DependencyList } from 'react'
// @ts-ignore
import { store } from '@sportninja/common/store'

/**
 * Lightweight useSelector replacement for react-redux@5.
 * Subscribes to the Redux store and re-renders when the selected value changes.
 *
 * The selector is re-run when the STORE changes — not when the component
 * re-renders. A selector that reads props or other state (e.g. a game that
 * loads after mount) must list those inputs in `deps`, or it keeps the value it
 * computed on mount until something unrelated is dispatched.
 */
export function useSelector<T>(
  selector: (state: any) => T,
  deps: DependencyList = []
): T {
  const [value, setValue] = useState<T>(() => selector(store.getState()))
  const selectorRef = useRef(selector)
  selectorRef.current = selector

  useEffect(() => {
    const checkForUpdates = () => {
      const newValue = selectorRef.current(store.getState())
      setValue((prev) => {
        if (prev === newValue) {
          return prev
        }
        return newValue
      })
    }

    const unsubscribe = store.subscribe(checkForUpdates)
    // Check once in case state changed between render and subscribe
    checkForUpdates()
    return unsubscribe
  }, [])

  // Re-read when the selector's own inputs change (see `deps` above). Without
  // deps the mount check above already covers it — callers that don't pass
  // any behave exactly as before.
  useEffect(() => {
    if (deps.length === 0) {
      return
    }
    const newValue = selectorRef.current(store.getState())
    setValue((prev) => (prev === newValue ? prev : newValue))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return value
}

/**
 * Lightweight useDispatch replacement for react-redux@5.
 */
export function useDispatch() {
  return store.dispatch
}
